import { Button } from '@/components/common/button'
import { TabsContent, TabsTrigger } from '@/components/common/tabs'
import { useConnectWalletModalContext } from '@/components/connect/connectModal'
import { TxAmountInput } from '@/components/opportunity/sidePanel/txAmountInput'
import { useTransaction } from '@/hooks/useTransaction'
import { type Action, useActionsQuery } from '@/queries/actions.query'
import { useTransactionsContextQuery } from '@/queries/transactionsContext.query'
import type { Vault } from '@/types/vault'
import { calculateTokenPriceInUsd } from '@/utils/calculateTokenPriceInUsd'
import { cn } from '@/utils/cn'
import { getTransactionStepData } from '@/utils/transactions/getTransactionStepData'
import { formatPreciseTokenValue, formatUSD } from '@vaultsfyi/common/utils'
import { useMemo, useState } from 'react'
import type { Address } from 'viem'
import { formatUnits } from 'viem'
import { AmountButtons } from '../sidePanel/amountButtons'
import { DepositLimitWarning } from '../sidePanel/depositLimitWarning'
import { TxSteps } from '../sidePanel/txSteps'
import { useDepositLimit } from '../sidePanel/useDepositLimit'
import { useMaxAmount } from '../sidePanel/useMaxAmount'
import { TxContextError } from '../txPanel/txContextError'
import { TxPanelSkeleton } from '../txPanel/txPanelSkeleton'
import { TxSubmissionGuard } from '../txPanel/txSubmissionGuard'
import type { TabConfig } from '../useTabsConfig'

export const LIDO_WRAP_EXCEPTION_ADDRESS = '0xae7ab96520DE3A18E5e111B5EaAb095312D7fE84'
export const LIDO_WRAP_TAB_NAME = 'lido-wrap'

const WSTETH_ADDRESS = '0x7f39C581F595B53c5cb19bD0b3f8dA6c935E2Ca0'
const STETH_LP_ADDRESS = '0xae7ab96520de3a18e5e111b5eaab095312d7fe84'

interface Props {
  account: Address | undefined
  vault: Vault
  tabConfig: TabConfig
  setTransactionFinished: ({ txHash }: { txHash: string | undefined; amount: string }) => void
  txState: 'idle' | 'loading' | 'success'
}

export const LidoWrappingTabTrigger = ({ className }: { className?: string }) => {
  return (
    <TabsTrigger value={LIDO_WRAP_TAB_NAME} className={className}>
      Wrap
    </TabsTrigger>
  )
}

export const LidoWrappingTab = ({ account, vault, tabConfig, setTransactionFinished, txState }: Props) => {
  const { asset, network } = vault
  const { getParsedAmount, walletBalance, form } = tabConfig

  const [useAllFunds, setUseAllFunds] = useState(false)
  const isWrap: boolean = form.watch('wrap') ?? true
  const setWrap = (wrap: boolean) => form.setValue('wrap', wrap)

  const wstEthVault = {
    ...vault,
    token: { ...asset, assetAddress: STETH_LP_ADDRESS },
    address: WSTETH_ADDRESS,
    vaultId: WSTETH_ADDRESS,
  }
  const {
    data: transactionsContext,
    isError: isContextError,
    isFetching: isContextFetching,
    refetch: refetchContext,
  } = useTransactionsContextQuery({
    address: account,
    vault: wstEthVault,
    initialData: undefined,
  })

  const actionLabel = isWrap ? 'wrap' : 'unwrap'
  const wrapActions = getTransactionStepData({ actions: transactionsContext, actionName: 'deposit' })
  const unwrapActions = getTransactionStepData({ actions: transactionsContext, actionName: 'redeem' })
  const activeActions = isWrap ? wrapActions : unwrapActions
  const inputAmount = form.watch('amount')
  const parsedAmount = getParsedAmount(inputAmount || '0')
  const displayedToken = isWrap ? 'stEth' : 'wstEth'
  const activeWalletBalance = isWrap ? walletBalance : (transactionsContext?.lpToken.balanceNative ?? '0')

  const { data: actions, isFetching } = useActionsQuery({
    address: account,
    action: activeActions?.name as Action,
    vault: wstEthVault,
    assetAddress: STETH_LP_ADDRESS,
    amount: parsedAmount,
    all: useAllFunds,
    enabled: !!transactionsContext && !isContextError,
  })

  const { setOpenModal: setOpenWalletModal } = useConnectWalletModalContext()

  const { executeTx, currentActionIndex } = useTransaction(network.name, actions, inputAmount, setTransactionFinished)

  const { depositLimit, depositLimitValue, isAboveDepositLimit } = useDepositLimit({
    transactionsContext,
    parsedAmount,
    assetDecimals: asset.decimals,
    isDepositAction: isWrap,
  })

  const error = useMemo(() => {
    if (BigInt(parsedAmount) > BigInt(activeWalletBalance)) {
      return `Not enough ${displayedToken} to ${actionLabel}`
    }
    return undefined
  }, [parsedAmount, activeWalletBalance])

  const onSubmit = form.handleSubmit(async () => {
    if (!account) {
      setOpenWalletModal('connect')
      return
    }
    if (error) {
      return
    }
    await executeTx()
  })

  const isSubmitDisabled = !activeActions || parsedAmount === '0' || !!error || isAboveDepositLimit
  const isSubmitLoading = isFetching || txState === 'loading'
  const { handleMaxClick } = useMaxAmount({
    form,
    maxAmount: activeWalletBalance,
    decimals: asset.decimals,
    setUseAllFunds,
  })

  const inputUsdValue = formatUSD(calculateTokenPriceInUsd(Number(inputAmount), Number(asset.assetPriceInUsd)) || '0')

  const balanceValue = formatPreciseTokenValue({
    value: activeWalletBalance,
    decimals: asset.decimals,
  })
  const isNonIdle = txState !== 'idle'

  return (
    <TxSubmissionGuard blocked={isContextError || !transactionsContext}>
      <form onSubmit={onSubmit}>
        <TabsContent value={LIDO_WRAP_TAB_NAME} className="mt-3 flex flex-col gap-3 md:mt-4 md:gap-4">
          {!transactionsContext && !isContextError && <TxPanelSkeleton />}
          {transactionsContext && (
            <>
              <div className={cn('mx-auto flex items-center gap-2', isNonIdle && 'hidden')}>
                <ActionToggle wrap={isWrap} setWrap={setWrap} />
              </div>

              <TxAmountInput
                amountForm={form}
                className={cn(isNonIdle && 'hidden')}
                usdValue={inputUsdValue}
                balance={balanceValue}
                asset={{ symbol: displayedToken }}
                hasError={!!error}
                onBalanceClick={handleMaxClick}
                onAmountChange={() => setUseAllFunds(false)}
              />

              {error && (
                <div className="rounded-2xl border border-destructive px-3 py-0.5 text-destructive">{error}</div>
              )}

              <AmountButtons
                form={form}
                maxInputAmount={activeWalletBalance}
                setUseAllFunds={setUseAllFunds}
                className={cn(isNonIdle && 'hidden')}
              />

              {isAboveDepositLimit && depositLimitValue && depositLimit !== undefined && (
                <DepositLimitWarning
                  depositLimitValue={depositLimitValue}
                  assetSymbol={asset.symbol}
                  actionLabel="Wrap"
                  onClick={() => {
                    form.setValue('amount', formatUnits(depositLimit, asset.decimals), { shouldValidate: true })
                    setUseAllFunds(false)
                  }}
                />
              )}

              <TxSteps
                account={account}
                stepData={activeActions}
                activeStep={currentActionIndex}
                loading={isSubmitLoading}
                disabled={isSubmitDisabled}
                label={actionLabel}
                network={network.name}
                success={txState === 'success'}
              />
            </>
          )}
          {isContextError && (
            <TxContextError
              variant={transactionsContext ? 'refresh' : 'initial'}
              isFetching={isContextFetching}
              onRetry={() => void refetchContext()}
            />
          )}
        </TabsContent>
      </form>
    </TxSubmissionGuard>
  )
}

interface ActionToggleProps {
  wrap: boolean
  setWrap: (wrap: boolean) => void
}

const ActionToggle = ({ wrap, setWrap }: ActionToggleProps) => {
  return (
    <div
      className={cn(
        'relative grid h-10 w-36 grid-cols-2 items-center gap-1 rounded-full border bg-page p-1',
        'before:pointer-events-none before:absolute before:left-1 before:my-1 before:h-[calc(2rem-2px)] before:w-[calc(100%/2-0.25rem)] before:rounded-full before:border before:border-accent/40 before:bg-accent before:bg-accent/10 before:text-accent before:transition-all',
        !wrap && 'before:translate-x-full'
      )}
    >
      <Button
        type="button"
        variant="default"
        size="default"
        onClick={() => setWrap(true)}
        className={cn('px-2 py-1', wrap && 'text-accent')}
      >
        Wrap
      </Button>
      <Button
        type="button"
        variant="default"
        size="default"
        onClick={() => setWrap(false)}
        className={cn('px-2 py-1', !wrap && 'text-accent')}
      >
        Unwrap
      </Button>
    </div>
  )
}
